import { cva, type VariantProps } from 'class-variance-authority'
import type { AnchorHTMLAttributes, ReactElement } from 'react'
import { ExternalGlyph } from '../internal/glyphs'
import { renderAs } from '../internal/render-as'
import { cn } from '../../utils/cn'

/**
 * A text link.
 *
 * Renders a plain anchor by default. An app with a router passes its own link
 * through `render`, which keeps the package free of a Next.js dependency:
 * The donor imported the app router directly, which is exactly what
 * stops a component being shareable.
 */
const linkVariants = cva(
    'focus-visible:ring-ring rounded-aria-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
    {
        variants: {
            tone: {
                default: 'text-foreground-accent font-medium underline underline-offset-[3px] hover:no-underline',
                subtle: 'text-foreground-muted hover:text-foreground',
                plain: 'text-inherit'
            },
            size: {
                small: 'text-aria-xs',
                medium: 'text-aria-base',
                large: 'text-aria-lg',
                inherit: ''
            }
        },
        defaultVariants: { tone: 'default', size: 'inherit' }
    }
)

/**
 * Leaving the product is a fact about the link, not a look, so it is a prop
 * rather than a tone.
 *
 * Three products each wrote an `ExternalLink` around their own anchor, and the
 * three disagreed about the two things that matter: one opened a tab without
 * `noreferrer`, which hands the opened page a handle on this one, and none of
 * them named the mark. `target` and `rel` are therefore not the caller's to set
 * on an external link. A caller who wants other values wants a plain link.
 *
 * The mark says "this leaves the site" to everyone who can see it, and the new
 * tab is a navigation the user did not ask for, so it has to be announced as
 * well as drawn. The package ships no copy, so the announcement is a prop, and
 * the union below makes the behaviour unreachable without it.
 */
type ExternalProps =
    | {
          isExternal: true
          /** Announces where the link goes, e.g. "opens in a new tab". */
          externalLabel: string
      }
    | { isExternal?: false; externalLabel?: never }

export type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'style' | 'target' | 'rel'> &
    VariantProps<typeof linkVariants> &
    ExternalProps & {
        /** An app's router link, rendered in place of the anchor. */
        render?: ReactElement
    }

export const Link = ({ tone, size, render, isExternal, externalLabel, children, ...props }: LinkProps) => {
    const className = cn(linkVariants({ tone, size }))

    /*
     * The anchor stays inline, and the mark is an inline glyph inside it.
     *
     * `inline-flex` would have been the shorter answer and it breaks the case
     * this component exists for: a link inside a sentence has to wrap between
     * its own words, and the mark has to travel with the last of them.
     */
    const content = isExternal ? (
        <>
            {children}
            <span className="sr-only"> {externalLabel}</span>
            <span className="ml-0.5 inline-block align-[-0.1em] no-underline" aria-hidden>
                <ExternalGlyph />
            </span>
        </>
    ) : (
        children
    )

    // Spread after the caller's props: these two are the contract the variant
    // exists to keep, not defaults a caller may quietly replace.
    const externalAttributes = isExternal ? { target: '_blank', rel: 'noreferrer noopener' } : {}

    if (render) return renderAs(render, { ...props, ...externalAttributes, className, children: content })

    return (
        <a {...props} {...externalAttributes} className={className}>
            {content}
        </a>
    )
}
